/**
 * Agent credential grants — identity credentials assigned to one agent.
 *
 * Once an agent holds ANY grant for a provider (AWS roles: `aws-role` and
 * `aws-sso` count as one provider), optr-identity lets it use ONLY its granted
 * credentials for that provider. Grants are not versioned with the agent.
 * Backend: optr-agent-core `/spaces/{spaceId}/agents/{agentId}/credentials`.
 */

export type CredentialOwnerKind = 'user' | 'space-service' | 'org-service-account';

export interface AgentCredentialGrant {
  agentId: string;
  orgId: string;
  spaceId?: string;
  credId: string;
  provider: string;
  /** Provider family from identity's catalog, e.g. `aws` for `aws-role`. Read via `credentialFamily`. */
  family?: string;
  providerInstanceId?: string;
  ownerKind: CredentialOwnerKind;
  displayName?: string;
  /** Non-secret display context, e.g. `{ accountId, accountName, roleName }`. */
  details?: Record<string, string>;
  isDefault?: boolean;
  grantedBy: string;
  grantedAt: string;
}

/** A credential that may be granted to the agent (picker option). */
export interface GrantableCredential {
  credId: string;
  provider: string;
  /** Provider family from identity's catalog, e.g. `aws` for `aws-role`. Read via `credentialFamily`. */
  family?: string;
  providerInstanceId?: string;
  ownerKind: CredentialOwnerKind;
  displayName?: string;
  status?: string;
  details?: Record<string, string>;
}

export interface AgentCredentialGrantInput {
  credId: string;
  isDefault?: boolean;
}

/**
 * Grants restrict a provider FAMILY (AWS IAM roles and AWS SSO are one, `aws`).
 * The family comes from optr-identity's provider catalog via the API; items
 * from a backend that does not send it yet are their own family.
 */
export function credentialFamily(c: { provider: string; family?: string }): string {
  return (c.family ?? c.provider).toLowerCase();
}
